import React, { useCallback, useEffect, useMemo } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useAppDispatch, useAppSelector } from 'hooks';

import { Button, ErrorResponse } from '~components';
import { TableView } from '~components/table-view-v3';
import useEditableTableRows, { EditableRow } from '~support-at-home/hooks/useEditableTableRows';

import columnsFactory from '~support-at-home/forms/client-services/listSingle';

import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import { create, deleteOne, getAll, update } from '~support-at-home/actions/clientServices';

import { selectSahCategories } from '~lookups/selectors/sahCategories';
import { selectSahClientServices } from '~support-at-home/selectors/clientServices';

import { filterRowFields } from '../helpers';

interface ComponentProps {
  id: number | null;
  isOpen?: boolean;
  onClose: (confirm: boolean) => void;
}

const ClientServicesModal: React.FC<ComponentProps> = ({ id, isOpen = false, onClose }) => {
  const dispatch = useAppDispatch();
  const clientServices = useAppSelector(selectSahClientServices);
  const categories = useAppSelector(selectSahCategories);
  const { data: servicesData } = useGetSahServicesQuery();

  const columns = useMemo(() => columnsFactory(servicesData ?? [], categories), [servicesData, categories]);

  const persistedServices: EditableRow[] = useMemo(
    () => clientServices.map((service) => ({ ...service, persisted: true, backendId: service.id })),
    [clientServices],
  );

  const getDefaultRow = useCallback(
    (): EditableRow => ({
      id: crypto.randomUUID(),
      client_id: id,
      service_id: '',
      start_date: '',
      end_date: '',
    }),
    [id],
  );

  const load = useCallback(() => {
    dispatch(getAll({ client_id: id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (isOpen && id !== null) {
      load();
    }
  }, [id, isOpen, load]);

  const { rows, tableRef, error, isProcessing, handleChange, canRowValidate, confirmChanges } = useEditableTableRows({
    source: persistedServices,
    getDefaultRow,
    onCreate: async (row) => {
      const toCreate = filterRowFields(row, columns, id);
      await dispatch(create(toCreate)).unwrap();
    },
    onUpdate: async (row) => {
      if (!row.backendId) return;
      const toUpdate = filterRowFields(row, columns, id);
      await dispatch(update({ ...toUpdate, id: row.backendId })).unwrap();
    },
    onDelete: async (row) => {
      if (!row.backendId) return;
      await dispatch(deleteOne({ id: row.backendId })).unwrap();
    },
  });

  const handleConfirm = async () => {
    const outcome = await confirmChanges();
    if (outcome === 'saved') {
      load();
      onClose(true);
    } else if (outcome === 'clean') {
      onClose(false);
    }
  };

  return (
    <Modal isOpen={isOpen} size="xl" style={{ maxWidth: '1500px', width: '100%' }} data-cy="client-services-modal">
      <ModalHeader className="bg-success text-white">Allocated SAH Services</ModalHeader>
      <ModalBody>
        <ErrorResponse message={error} dataCy="client-services-modal-error" />
        <TableView
          ref={tableRef}
          id="client-services-modal"
          columns={columns}
          data={rows}
          editonly
          onChange={handleChange}
          canRowValidate={canRowValidate}
          canDeleteRow
          showAddRow
          emptyMessage="No data found"
          bordered
          compactView
        />
      </ModalBody>
      <ModalFooter className="justify-content-end">
        <Button
          size="sm"
          color="success"
          className="ps-4 pe-4 mx-2"
          onClick={handleConfirm}
          loading={isProcessing}
          dataCy="client-services-modal-confirm-button"
        >
          Confirm
        </Button>
        <Button
          size="sm"
          className="ps-4 pe-4 mx-2"
          onClick={() => onClose(false)}
          disabled={isProcessing}
          dataCy="client-services-modal-cancel-button"
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClientServicesModal;
